/**
 * video -- Sub-modo embebible dentro de stepper-flow.
 *
 * Detecta YouTube vs MP4 (heuristica por URL) y embebe con controles nativos
 * o iframe sin tracking innecesario. Aspect ratio 16:9 mantenido con padding-bottom.
 *
 * stepData:
 *   - src          (string, requerido) - URL del video (YouTube watch/embed/short, MP4 directo)
 *   - title        (string, opcional)  - heading del paso
 *   - caption      (string, opcional)  - texto descriptivo bajo el video
 *   - autoplay     (boolean, opcional) - default false (cumple politicas browser anti-autoplay)
 *
 * stepCtx (provisto por stepper-flow):
 *   - escHtml(s), recordResult(data)
 *
 * @module presentation/components/ActionAreaModes/video
 */

const YT_PATTERNS = [
  /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([\w-]{11})/i
];

function extractYouTubeId(url) {
  if (!url) return null;
  for (const pat of YT_PATTERNS) {
    const m = url.match(pat);
    if (m) return m[1];
  }
  return null;
}

function isDirectVideo(url) {
  return /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url || '');
}

export function renderVideo(container, stepData, stepCtx) {
  const src = stepData?.src || '';
  const title = stepData?.title || '';
  const caption = stepData?.caption || '';
  const autoplay = stepData?.autoplay === true;

  if (!src) {
    container.innerHTML = `<div style="padding:14px;color:#f85149;font-size:0.875rem">video sub-modo: falta src</div>`;
    stepCtx.recordResult({ played: false, error: 'no-src' });
    return;
  }

  const ytId = extractYouTubeId(src);
  const direct = isDirectVideo(src);

  // Aspect ratio 16:9 wrapper
  const aspectWrapper = (innerHtml) => `
    <div style="position:relative;width:100%;padding-bottom:56.25%;background:#000;border-radius:6px;overflow:hidden;border:1px solid #30363d">
      ${innerHtml}
    </div>
  `;

  let mediaHtml;
  if (ytId) {
    const params = new URLSearchParams({
      modestbranding: '1',
      rel: '0',
      playsinline: '1'
    });
    if (autoplay) params.set('autoplay', '1');
    const embedSrc = `https://www.youtube-nocookie.com/embed/${ytId}?${params}`;
    mediaHtml = aspectWrapper(`
      <iframe src="${stepCtx.escHtml(embedSrc)}"
              style="position:absolute;inset:0;width:100%;height:100%;border:0"
              allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              loading="lazy"
              data-aa-substep-yt-id="${stepCtx.escHtml(ytId)}"></iframe>
    `);
  } else if (direct) {
    mediaHtml = aspectWrapper(`
      <video controls preload="metadata" ${autoplay ? 'autoplay muted' : ''}
             style="position:absolute;inset:0;width:100%;height:100%;background:#000;object-fit:contain"
             data-aa-substep-video>
        <source src="${stepCtx.escHtml(src)}"/>
        Tu navegador no soporta el tag video.
      </video>
    `);
  } else {
    // Fallback: link clickeable + warning
    mediaHtml = `
      <div style="padding:14px;background:#161b22;border:1px dashed #d29922;border-radius:6px;color:#d29922;font-size:0.875rem">
        URL no reconocida como YouTube ni video directo (.mp4/.webm). Abrir en pestana nueva:
        <a href="${stepCtx.escHtml(src)}" target="_blank" rel="noopener noreferrer"
           style="color:#79c0ff;text-decoration:underline;margin-left:6px">${stepCtx.escHtml(src)}</a>
      </div>
    `;
  }

  container.innerHTML = `
    <div class="aa-step-video" data-aa-substep-type="video">
      ${title ? `<h4 style="margin:0 0 10px;color:#f0f6fc;font-size:0.98rem">${stepCtx.escHtml(title)}</h4>` : ''}
      ${mediaHtml}
      ${caption ? `<p style="margin:10px 0 0;color:#8b949e;font-size:0.875rem;line-height:1.45">${stepCtx.escHtml(caption)}</p>` : ''}
    </div>
  `;

  // Listener opcional: cuando el video MP4 termina, marcar como visto
  const $vid = container.querySelector('[data-aa-substep-video]');
  if ($vid) {
    $vid.addEventListener('ended', () => stepCtx.recordResult({ played: true, completed: true }));
    $vid.addEventListener('play', () => stepCtx.recordResult({ played: true, completed: false }), { once: true });
  } else {
    // YT iframe: no podemos saber si lo vio sin la iframe API; lo marcamos como mostrado
    stepCtx.recordResult({ played: false, shown: true });
  }
}
